import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gamepad2, Plus, X, Loader2, Palette, Hash, ToggleLeft, List, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";

const ICONS = ["Gamepad2", "Dice5", "Spade", "Heart", "Diamond", "Club", "Crown", "Star", "Target", "Zap"];
const COLORS = ["#C6A87C", "#F61225", "#4DFFFF", "#8B4513", "#9333EA", "#059669", "#2D3748", "#F308C9"];

const SCORING_TYPES = [
  { value: "points_low_wins", label: "Düşük puan kazanır", description: "Ceza puanı sistemi (101 Okey gibi)" },
  { value: "points_high_wins", label: "Yüksek puan kazanır", description: "Skor toplama sistemi (Pişti gibi)" },
  { value: "wins_count", label: "Kazanma sayısı", description: "Galibiyet sayısı sistemi (Tavla gibi)" },
];

const FIELD_TYPES = [
  { value: "number", label: "Sayı", icon: Hash },
  { value: "boolean", label: "Evet/Hayır", icon: ToggleLeft },
  { value: "select", label: "Seçim Listesi", icon: List },
];

export default function CreateCustomGame() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    name_tr: "",
    description: "",
    icon: "Gamepad2",
    color: "#C6A87C",
    min_players: 2,
    max_players: 4,
    scoring_type: "points_high_wins",
    ai_prompt_style: "",
  });
  const [fields, setFields] = useState([
    { name: "total_score", label: "Toplam Puan", field_type: "number", default_value: 0, options: [] }
  ]);

  const addField = () => {
    setFields([...fields, { 
      name: `field_${fields.length + 1}`, 
      label: "", 
      field_type: "number", 
      default_value: 0,
      options: []
    }]);
  };

  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };

  const updateField = (index, key, value) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    
    // Auto-generate name from label
    if (key === "label") {
      newFields[index].name = value
        .toLowerCase()
        .replace(/[^a-zğüşıöç0-9\s]/gi, '')
        .replace(/\s+/g, '_')
        .replace(/[ğ]/g, 'g')
        .replace(/[ü]/g, 'u')
        .replace(/[ş]/g, 's')
        .replace(/[ı]/g, 'i')
        .replace(/[ö]/g, 'o')
        .replace(/[ç]/g, 'c');
    }
    
    setFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Oyun adı gerekli");
      return;
    }
    
    if (fields.length === 0 || !fields[0].label) {
      toast.error("En az bir alan tanımlayın");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        name_tr: formData.name_tr || formData.name,
        fields: fields.map(f => ({
          name: f.name,
          label: f.label,
          field_type: f.field_type,
          default_value: f.field_type === "boolean" ? false : (f.field_type === "number" ? 0 : ""),
          options: f.field_type === "select" ? f.options : null
        })),
        ai_prompt_style: formData.ai_prompt_style || `Türkçe spor yorumcusu tarzında ${formData.name} oyunu hakkında yorum yap.`
      };
      
      const response = await api.createCustomGame(payload);
      toast.success("Oyun başarıyla oluşturuldu!");
      navigate(`/create-league?game=${response.data.game_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Oyun oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-custom-game-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Özel Oyun Oluştur
        </h1>
        <p className="text-[#B1C1C1] mt-1">Kendi oyun şablonunu tanımla</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Gamepad2 className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Temel Bilgiler
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-[#B1C1C1]">Oyun Adı (İngilizce)</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Rummy"
                className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
                required
                data-testid="game-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="name_tr" className="text-[#B1C1C1]">Oyun Adı (Türkçe)</Label>
              <Input
                id="name_tr"
                value={formData.name_tr}
                onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })}
                placeholder="Örn: Okey"
                className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
                data-testid="game-name-tr-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description" className="text-[#B1C1C1]">Açıklama</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Oyunun kısa açıklaması..."
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white"
              rows={2}
            />
          </div>
          
          {/* Icon and Color */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#B1C1C1]">İkon</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-all ${
                      formData.icon === icon
                        ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <Gamepad2 className="w-5 h-5 text-white" />
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-[#B1C1C1]">Renk</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      formData.color === color
                        ? "border-white scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Game Settings */}
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-[#4DFFFF]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Oyun Ayarları
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="min_players" className="text-[#B1C1C1]">Min Oyuncu</Label>
              <Input
                id="min_players"
                type="number"
                min="2"
                max="10"
                value={formData.min_players}
                onChange={(e) => setFormData({ ...formData, min_players: parseInt(e.target.value) })}
                className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
              />
            </div>
            
            <div>
              <Label htmlFor="max_players" className="text-[#B1C1C1]">Max Oyuncu</Label>
              <Input
                id="max_players"
                type="number"
                min="2"
                max="10"
                value={formData.max_players}
                onChange={(e) => setFormData({ ...formData, max_players: parseInt(e.target.value) })}
                className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
              />
            </div>
            
            <div>
              <Label className="text-[#B1C1C1]">Skor Sistemi</Label>
              <Select
                value={formData.scoring_type}
                onValueChange={(value) => setFormData({ ...formData, scoring_type: value })}
              >
                <SelectTrigger className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A1635] border-white/10">
                  {SCORING_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <p className="text-xs text-[#64748B]">
            {SCORING_TYPES.find(t => t.value === formData.scoring_type)?.description}
          </p>
        </div>

        {/* Custom Fields */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-[#C6A87C]" />
              <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Skor Alanları
              </h2>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addField}
              className="border-white/20 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Alan Ekle
            </Button>
          </div>
          
          <p className="text-sm text-[#64748B] mb-4">
            Her hafta oyuncular için kaydedilecek alanları tanımlayın
          </p>
          
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={index} className="p-4 rounded-lg bg-white/5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#C6A87C] font-medium">Alan {index + 1}</span>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="p-1 hover:bg-white/10 rounded text-[#64748B] hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-[#64748B]">Alan Adı</Label>
                    <Input
                      value={field.label}
                      onChange={(e) => updateField(index, "label", e.target.value)}
                      placeholder="Örn: Toplam Puan"
                      className="mt-1 bg-[#050E2E]/50 border-white/10 text-white h-10 text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-[#64748B]">Tip</Label>
                    <Select
                      value={field.field_type}
                      onValueChange={(value) => updateField(index, "field_type", value)}
                    >
                      <SelectTrigger className="mt-1 bg-[#050E2E]/50 border-white/10 text-white h-10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A1635] border-white/10">
                        {FIELD_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} className="text-white text-sm">
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {field.field_type === "select" && (
                    <div>
                      <Label className="text-xs text-[#64748B]">Seçenekler (virgülle)</Label>
                      <Input
                        value={field.options?.join(", ") || ""}
                        onChange={(e) => updateField(index, "options", e.target.value.split(",").map(o => o.trim()))}
                        placeholder="Seçenek1, Seçenek2"
                        className="mt-1 bg-[#050E2E]/50 border-white/10 text-white h-10 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Prompt */}
        <div className="glass-card p-6">
          <Label htmlFor="ai_prompt" className="text-[#B1C1C1]">AI Yorum Stili (Opsiyonel)</Label>
          <Textarea
            id="ai_prompt"
            value={formData.ai_prompt_style}
            onChange={(e) => setFormData({ ...formData, ai_prompt_style: e.target.value })}
            placeholder="Türkçe spor yorumcusu tarzında, strateji ve ustalık vurgulu..."
            className="mt-2 bg-[#050E2E]/50 border-white/10 text-white"
            rows={2}
          />
          <p className="text-xs text-[#64748B] mt-1">
            AI'ın haftalık yorumları nasıl yapacağını belirler
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-12"
          data-testid="create-game-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Oluşturuluyor...
            </>
          ) : (
            <>
              <Gamepad2 className="w-4 h-4 mr-2" />
              Oyun Oluştur
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
